/**
 * The `jualan` wordmark lockup — the one thing that separates this sub-brand
 * from lapakbangade.com in a tab strip.
 *
 * Three surfaces need the same lockup in different clothing: the white public
 * header, the accent band/header, and the ink admin sidebar. They are variants
 * of one component rather than three hand-built copies because the ratio the
 * brand rules care about (kicker always narrower than the wordmark, box always
 * on the baseline) is easy to break by hand and impossible to break here.
 *
 * This file is the single sanctioned home for raw hex in .tsx: SVG attributes
 * in `BrandFaviconSvg` are written to a standalone file that never sees
 * Tailwind, so they cannot go through a token. Everything else in `src/`
 * resolves colors through the @theme tokens, and theme.test.ts enforces it.
 */

export type BrandVariant = 'light' | 'accent' | 'admin';
export type BrandSize = 'sm' | 'md';

interface BrandMarkProps {
  variant?: BrandVariant;
  size?: BrandSize;
  className?: string;
}

/** Per-surface colours. The wordmark is never accent-coloured: 1.73:1 on white. */
const VARIANTS: Record<BrandVariant, { word: string; box: string; kicker: string; text: string }> = {
  light: { word: 'text-ink', box: 'bg-accent', kicker: 'text-muted', text: 'LAPAK BANG ADE' },
  accent: { word: 'text-ink', box: 'bg-white', kicker: 'text-ink-2', text: 'LAPAK BANG ADE' },
  admin: { word: 'text-white', box: 'bg-accent', kicker: 'text-muted-dark', text: 'PANEL ADMIN' },
};

const SIZES: Record<BrandSize, { word: string; box: string; kicker: string }> = {
  md: { word: 'text-[23px]', box: 'w-[7px] h-[7px]', kicker: 'text-[9px]' },
  sm: { word: 'text-[19px]', box: 'w-[6px] h-[6px]', kicker: 'text-[8.5px]' },
};

export function BrandMark({ variant = 'light', size = 'md', className = '' }: BrandMarkProps) {
  const v = VARIANTS[variant];
  const s = SIZES[size];

  return (
    <span className={`inline-flex flex-col gap-[1px] ${className}`}>
      <span className="flex items-baseline gap-[4px]">
        <span className={`font-extrabold leading-none tracking-[-0.04em] ${s.word} ${v.word}`}>
          jualan
        </span>
        <span className={`inline-block rounded-[2px] ${s.box} ${v.box}`} aria-hidden="true" />
      </span>
      <span className={`font-mono font-semibold tracking-[0.11em] ${s.kicker} ${v.kicker}`}>
        {v.text}
      </span>
    </span>
  );
}

/**
 * The favicon/OG mark as an SVG string, so build-time scripts and other agents
 * can write it to `public/` without importing React.
 *
 * ponytail: the `j` is a <text> node, which renders with whatever font the
 * consumer has. Browsers rasterising the favicon fall back to a generic sans
 * and the mark still reads as a `j`. Convert to a path only if the fallback
 * shape ever looks wrong in a shipped icon.
 */
export function BrandFaviconSvg(size = 64): string {
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64" role="img" aria-label="jualan">`,
    '<rect width="64" height="64" rx="18" fill="#0f172a"/>',
    '<text x="27" y="46" font-family="Nunito, ui-sans-serif, sans-serif" font-size="42" font-weight="800" fill="#ffffff" text-anchor="middle">j</text>',
    '<rect x="40" y="38" width="8" height="8" rx="2" fill="#ffb800"/>',
    '</svg>',
  ].join('');
}

export default BrandMark;
